import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Drawer, Tooltip, Skeleton, Empty, Switch } from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, MenuOutlined,
  SearchOutlined, ReloadOutlined, GlobalOutlined,
  AppstoreOutlined, LinkOutlined,
} from "@ant-design/icons";
import { authHeaders } from "../utils/session";

const authH = () => ({ ...authHeaders() });

const inputStyle = (err) => ({
  width: "100%", height: "42px", padding: "0 14px", borderRadius: "10px",
  border: `1.5px solid ${err ? "#ef4444" : "#e5e7eb"}`,
  fontSize: "14px", outline: "none", color: "#111827",
  boxShadow: err ? "0 0 0 3px rgba(239,68,68,0.1)" : "none",
  boxSizing: "border-box",
});

const labelStyle = {
  display: "block", fontSize: "12px", fontWeight: 700, color: "#374151",
  marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px",
};

/* ── FormField helper ─────────────────────────────────────────────────────── */
const Field = ({ label, required, hint, error, children }) => (
  <div style={{ marginBottom: "18px" }}>
    <label style={labelStyle}>
      {label}
      {required && <span style={{ color: "#ef4444" }}> *</span>}
      {hint && <span style={{ fontSize: "11px", fontWeight: 400, color: "#9ca3af", textTransform: "none", letterSpacing: 0, marginLeft: "4px" }}>{hint}</span>}
    </label>
    {children}
    {error && <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px" }}>{error}</div>}
  </div>
);

/* ── Main Component ──────────────────────────────────────────────────────── */
const ManageCmsMenus = () => {
  const [menus, setMenus]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState("");
  const [expandedIds, setExpandedIds] = useState(new Set());

  // ── Menu Drawer ───────────────────────────────────────────────────────────
  const [menuDrawer, setMenuDrawer]   = useState(false);
  const [editMenuId, setEditMenuId]   = useState(null);
  const [menuSaving, setMenuSaving]   = useState(false);
  const [menuForm, setMenuForm]       = useState({ menu_name: "", menu_icon: "", menu_link: "", menu_order: "", status: 1 });
  const [menuErrors, setMenuErrors]   = useState({});

  // ── Sub-menu Drawer ───────────────────────────────────────────────────────
  const [subDrawer, setSubDrawer]     = useState(false);
  const [editSubId, setEditSubId]     = useState(null);
  const [parentMenuId, setParentMenuId] = useState(null);
  const [subSaving, setSubSaving]     = useState(false);
  const [subForm, setSubForm]         = useState({ sub_menu_name: "", sub_menu_link: "", sub_menu_icon: "", sub_menu_order: "", status: 1 });
  const [subErrors, setSubErrors]     = useState({});

  // ── Fetch menus ───────────────────────────────────────────────────────────
  const fetchMenus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/cms-menus`, { headers: authH() });
      // console.log("from manage-cms-menu", res);
      
      setMenus(res.data.data || []);
    } catch { toast.error("Failed to load menus"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMenus(); }, [fetchMenus]);

  // ── Expand/collapse ───────────────────────────────────────────────────────
  const toggleExpand = (id) => setExpandedIds(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  // ── Menu CRUD ─────────────────────────────────────────────────────────────
  const openAddMenu = () => {
    setEditMenuId(null);
    setMenuForm({ menu_name: "", menu_icon: "", menu_link: "", menu_order: "", status: 1 });
    setMenuErrors({});
    setMenuDrawer(true);
  };

  const openEditMenu = (menu) => {
    setEditMenuId(menu.id);
    setMenuForm({
      menu_name: menu.menu_name || "",
      menu_icon: menu.menu_icon || "",
      menu_link: menu.menu_link || "",
      menu_order: menu.menu_order ?? "",
      status: menu.status ?? 1,
    });
    setMenuErrors({});
    setMenuDrawer(true);
  };

  const handleDeleteMenu = async (id, name) => {
    const r = await Swal.fire({
      title: `Delete "${name}"?`,
      text: "This will also delete all sub-menus and remove role access for this menu.",
      icon: "warning", showCancelButton: true,
      confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete",
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API_BASE_URL}/cms-menus/${id}`, { headers: authH() });
      toast.success("Menu deleted successfully");
      fetchMenus();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete menu");
    }
  };

  const handleSaveMenu = async () => {
    if (!menuForm.menu_name.trim()) { setMenuErrors({ menu_name: "Menu name is required" }); return; }
    setMenuErrors({});
    const payload = {
      menu_name: menuForm.menu_name.trim(),
      ...(menuForm.menu_icon.trim()  && { menu_icon: menuForm.menu_icon.trim() }),
      ...(menuForm.menu_link.trim()  && { menu_link: menuForm.menu_link.trim() }),
      ...(menuForm.menu_order !== "" && { menu_order: parseInt(menuForm.menu_order) }),
      ...(editMenuId               && { status: menuForm.status }),
    };
    setMenuSaving(true);
    try {
      if (editMenuId) {
        await axios.put(`${API_BASE_URL}/cms-menus/${editMenuId}`, payload, { headers: { "Content-Type": "application/json", ...authH() } });
        toast.success("Menu updated successfully");
      } else {
        await axios.post(`${API_BASE_URL}/cms-menus`, payload, { headers: { "Content-Type": "application/json", ...authH() } });
        toast.success("Menu created successfully");
      }
      setMenuDrawer(false);
      fetchMenus();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save menu");
    } finally { setMenuSaving(false); }
  };

  // ── Sub-menu CRUD ─────────────────────────────────────────────────────────
  const openAddSub = (menuId) => {
    setEditSubId(null);
    setParentMenuId(menuId);
    setSubForm({ sub_menu_name: "", sub_menu_link: "", sub_menu_icon: "", sub_menu_order: "", status: 1 });
    setSubErrors({});
    setSubDrawer(true);
    // Auto-expand parent
    setExpandedIds(prev => new Set([...prev, menuId]));
  };

  const openEditSub = (sub, menuId) => {
    setEditSubId(sub.id);
    setParentMenuId(menuId);
    setSubForm({
      sub_menu_name: sub.sub_menu_name || "",
      sub_menu_link: sub.sub_menu_link || "",
      sub_menu_icon: sub.sub_menu_icon || "",
      sub_menu_order: sub.sub_menu_order ?? "",
      status: sub.status ?? 1,
    });
    setSubErrors({});
    setSubDrawer(true);
  };

  const handleDeleteSub = async (id, name) => {
    const r = await Swal.fire({
      title: `Delete "${name}"?`, text: "This action cannot be undone.",
      icon: "warning", showCancelButton: true,
      confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete",
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API_BASE_URL}/cms-sub-menus/${id}`, { headers: authH() });
      toast.success("Sub-menu deleted successfully");
      fetchMenus();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete sub-menu");
    }
  };

  const handleSaveSub = async () => {
    if (!subForm.sub_menu_name.trim()) { setSubErrors({ sub_menu_name: "Sub-menu name is required" }); return; }
    setSubErrors({});
    const payload = {
      cms_menu_id: parentMenuId,
      sub_menu_name: subForm.sub_menu_name.trim(),
      ...(subForm.sub_menu_link.trim()  && { sub_menu_link: subForm.sub_menu_link.trim() }),
      ...(subForm.sub_menu_icon.trim()  && { sub_menu_icon: subForm.sub_menu_icon.trim() }),
      ...(subForm.sub_menu_order !== "" && { sub_menu_order: parseInt(subForm.sub_menu_order) }),
      ...(editSubId                     && { status: subForm.status }),
    };
    setSubSaving(true);
    try {
      if (editSubId) {
        await axios.put(`${API_BASE_URL}/cms-sub-menus/${editSubId}`, payload, { headers: { "Content-Type": "application/json", ...authH() } });
        toast.success("Sub-menu updated successfully");
      } else {
        await axios.post(`${API_BASE_URL}/cms-sub-menus`, payload, { headers: { "Content-Type": "application/json", ...authH() } });
        toast.success("Sub-menu created successfully");
      }
      setSubDrawer(false);
      fetchMenus();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save sub-menu");
    } finally { setSubSaving(false); }
  };

  // ── Computed ──────────────────────────────────────────────────────────────
  const filtered    = menus.filter(m => m.menu_name?.toLowerCase().includes(search.toLowerCase()));
  const activeCount = menus.filter(m => m.status === 1).length;
  const totalSubs   = menus.reduce((acc, m) => acc + (m.sub_menus?.length || 0), 0);
  const parentMenuName = menus.find(m => m.id === parentMenuId)?.menu_name || "";

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* ── Page Header ── */}
          <div style={{
            background: "linear-gradient(135deg,#0f172a 0%,#1e3a5f 50%,#1d4ed8 100%)",
            borderRadius: "16px", padding: "28px 32px", marginBottom: "28px",
            boxShadow: "0 8px 32px rgba(29,78,216,0.28)",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            flexWrap: "wrap", gap: "16px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{
                width: "56px", height: "56px", background: "rgba(255,255,255,.22)",
                borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "26px", color: "white",
              }}>
                <MenuOutlined />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: "24px", fontWeight: 700 }}>Manage CMS Menus</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: "14px" }}>Configure sidebar navigation menus and sub-menus</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <button onClick={fetchMenus} style={{
                width: "40px", height: "40px", borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)",
                color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px",
              }}>
                <ReloadOutlined />
              </button>
              <button onClick={openAddMenu} style={{
                display: "flex", alignItems: "center", gap: "7px", height: "40px",
                padding: "0 20px", borderRadius: "10px", border: "none",
                background: "white", color: "#1d4ed8", fontWeight: 700, fontSize: "14px",
                cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}>
                <PlusOutlined /> Add Menu
              </button>
            </div>
          </div>

          {/* ── Stats + Search ── */}
          <div style={{
            background: "white", borderRadius: "16px",
            boxShadow: "0 2px 16px rgba(0,0,0,.07)", border: "1px solid #f0f0f0",
            marginBottom: "24px", padding: "20px 24px",
            display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap",
          }}>
            {[
              { label: "Total Menus", value: menus.length,  icon: <MenuOutlined />,    color: "#1d4ed8", bg: "#eff6ff" },
              { label: "Active",      value: activeCount,   icon: <GlobalOutlined />,  color: "#16a34a", bg: "#f0fdf4" },
              { label: "Sub-Menus",   value: totalSubs,     icon: <AppstoreOutlined />,color: "#7c3aed", bg: "#f5f3ff" },
            ].map(s => (
              <div key={s.label} style={{
                display: "flex", alignItems: "center", gap: "12px", minWidth: "130px",
                padding: "10px 16px", background: s.bg, borderRadius: "10px",
              }}>
                <div style={{
                  width: "36px", height: "36px", background: "white", borderRadius: "9px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: s.color, fontSize: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: "20px", fontWeight: 800, color: "#111827", lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500, marginTop: "2px" }}>{s.label}</div>
                </div>
              </div>
            ))}
            <div style={{ flex: 1, minWidth: "200px", marginLeft: "auto" }}>
              <div style={{ position: "relative" }}>
                <SearchOutlined style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "14px" }} />
                <input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search menus..."
                  style={{
                    width: "100%", height: "42px", paddingLeft: "36px", paddingRight: "16px",
                    border: "1px solid #e5e7eb", borderRadius: "10px", fontSize: "14px",
                    outline: "none", background: "white", color: "#374151", boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
          </div>

          {/* ── Menu List ── */}
          {loading ? (
            <div style={{ background: "white", borderRadius: "16px", padding: "24px", border: "1px solid #f0f0f0" }}>
              {[1,2,3].map(i => <Skeleton key={i} active paragraph={{ rows: 2 }} style={{ marginBottom: "16px" }} />)}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ background: "white", borderRadius: "16px", padding: "60px 24px", border: "1px solid #f0f0f0", textAlign: "center", boxShadow: "0 2px 16px rgba(0,0,0,.07)" }}>
              <Empty description={<span style={{ color: "#9ca3af" }}>{search ? "No menus match your search" : "No menus yet. Create your first menu."}</span>} />
              {!search && (
                <button onClick={openAddMenu} style={{ marginTop: "16px", padding: "10px 24px", borderRadius: "10px", border: "none", background: "linear-gradient(135deg,#1d4ed8,#0f172a)", color: "white", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}>
                  <PlusOutlined style={{ marginRight: "6px" }} /> Create First Menu
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {filtered.map(menu => {
                const isExpanded = expandedIds.has(menu.id);
                const hasSubs    = menu.sub_menus?.length > 0;
                return (
                  <div key={menu.id} style={{
                    background: "white", borderRadius: "14px",
                    border: "1px solid #f0f0f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    overflow: "hidden",
                  }}>
                    {/* ── Menu row ── */}
                    <div style={{
                      display: "flex", alignItems: "center", gap: "14px", padding: "16px 20px",
                      borderBottom: isExpanded && hasSubs ? "1px solid #f3f4f6" : "none",
                    }}>
                      {/* Icon */}
                      <div style={{
                        width: "46px", height: "46px", borderRadius: "12px", flexShrink: 0,
                        background: "linear-gradient(135deg,#1d4ed8dd,#1d4ed866)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "20px", color: "white",
                      }}>
                        {menu.menu_icon ? <i className={`fas ${menu.menu_icon.startsWith("fa-") ? menu.menu_icon : `fa-${menu.menu_icon}`}`} /> : <MenuOutlined />}
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827", marginBottom: "4px" }}>
                          {menu.menu_name}
                          <span style={{ fontSize: "11px", fontWeight: 500, color: "#9ca3af", marginLeft: "8px" }}>Order: {menu.menu_order ?? "—"}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
                          {menu.menu_link && (
                            <span style={{ fontSize: "12px", color: "#6b7280", display: "flex", alignItems: "center", gap: "4px" }}>
                              <LinkOutlined style={{ fontSize: "11px" }} />
                              <code style={{ fontSize: "11px", background: "#f3f4f6", padding: "1px 6px", borderRadius: "4px" }}>{menu.menu_link}</code>
                            </span>
                          )}
                          {menu.menu_icon && (
                            <span style={{ fontSize: "11px", color: "#9ca3af", fontFamily: "monospace", background: "#f9fafb", padding: "1px 8px", borderRadius: "4px" }}>
                              {menu.menu_icon}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                        <span style={{
                          fontSize: "11px", padding: "3px 10px", borderRadius: "10px", fontWeight: 700,
                          background: menu.status === 1 ? "#f0fdf4" : "#f9fafb",
                          color: menu.status === 1 ? "#16a34a" : "#9ca3af",
                        }}>
                          {menu.status === 1 ? "Active" : "Inactive"}
                        </span>

                        {/* Expand sub-menus */}
                        {hasSubs && (
                          <button
                            onClick={() => toggleExpand(menu.id)}
                            style={{
                              height: "32px", padding: "0 12px", borderRadius: "8px",
                              border: "1px solid #e0eaff", background: "#f0f5ff", color: "#2563eb",
                              cursor: "pointer", fontSize: "12px", fontWeight: 600,
                              display: "flex", alignItems: "center", gap: "5px",
                            }}
                          >
                            <AppstoreOutlined />
                            {menu.sub_menus.length}
                            <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`} style={{ fontSize: "10px" }} />
                          </button>
                        )}

                        <Tooltip title="Add Sub-menu">
                          <button onClick={() => openAddSub(menu.id)} style={{
                            width: "32px", height: "32px", borderRadius: "8px", border: "1px solid #dcfce7",
                            background: "#f0fdf4", color: "#16a34a", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px",
                          }}>
                            <PlusOutlined />
                          </button>
                        </Tooltip>

                        <Tooltip title="Edit Menu">
                          <button onClick={() => openEditMenu(menu)} style={{
                            width: "32px", height: "32px", borderRadius: "8px", border: "1px solid #e0eaff",
                            background: "#f0f5ff", color: "#2563eb", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px",
                          }}>
                            <EditOutlined />
                          </button>
                        </Tooltip>

                        <Tooltip title="Delete Menu">
                          <button onClick={() => handleDeleteMenu(menu.id, menu.menu_name)} style={{
                            width: "32px", height: "32px", borderRadius: "8px", border: "1px solid #fecaca",
                            background: "#fef2f2", color: "#dc2626", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px",
                          }}>
                            <DeleteOutlined />
                          </button>
                        </Tooltip>
                      </div>
                    </div>

                    {/* ── Sub-menus ── */}
                    {isExpanded && hasSubs && (
                      <div style={{ padding: "10px 20px 14px", background: "#fafafa" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px" }}>
                          Sub-menus ({menu.sub_menus.length})
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          {menu.sub_menus.map(sub => (
                            <div key={sub.id} style={{
                              display: "flex", alignItems: "center", gap: "12px",
                              padding: "10px 14px", background: "white", borderRadius: "10px",
                              border: "1px solid #f0f0f0",
                            }}>
                              <div style={{
                                width: "34px", height: "34px", borderRadius: "9px", flexShrink: 0,
                                background: "linear-gradient(135deg,#7c3aeddd,#7c3aed66)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "14px", color: "white",
                              }}>
                                {sub.sub_menu_icon
                                  ? <i className={`fas ${sub.sub_menu_icon.startsWith("fa-") ? sub.sub_menu_icon : `fa-${sub.sub_menu_icon}`}`} />
                                  : <AppstoreOutlined />
                                }
                              </div>

                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#374151" }}>
                                  {sub.sub_menu_name}
                                  <span style={{ fontSize: "11px", fontWeight: 400, color: "#9ca3af", marginLeft: "6px" }}>Order: {sub.sub_menu_order ?? "—"}</span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "2px" }}>
                                  {sub.sub_menu_link && (
                                    <span style={{ fontSize: "11px", color: "#9ca3af", display: "flex", alignItems: "center", gap: "3px" }}>
                                      <LinkOutlined style={{ fontSize: "10px" }} />
                                      <code style={{ fontSize: "10px", background: "#f3f4f6", padding: "1px 5px", borderRadius: "3px" }}>{sub.sub_menu_link}</code>
                                    </span>
                                  )}
                                  {sub.sub_menu_icon && (
                                    <span style={{ fontSize: "10px", color: "#9ca3af", fontFamily: "monospace", background: "#f9fafb", padding: "1px 6px", borderRadius: "3px" }}>
                                      {sub.sub_menu_icon}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                                <span style={{
                                  fontSize: "10px", padding: "2px 8px", borderRadius: "8px", fontWeight: 700,
                                  background: sub.status === 1 ? "#f0fdf4" : "#f9fafb",
                                  color: sub.status === 1 ? "#16a34a" : "#9ca3af",
                                }}>
                                  {sub.status === 1 ? "Active" : "Inactive"}
                                </span>
                                <Tooltip title="Edit Sub-menu">
                                  <button onClick={() => openEditSub(sub, menu.id)} style={{
                                    width: "28px", height: "28px", borderRadius: "7px", border: "1px solid #e0eaff",
                                    background: "#f0f5ff", color: "#2563eb", cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px",
                                  }}>
                                    <EditOutlined />
                                  </button>
                                </Tooltip>
                                <Tooltip title="Delete Sub-menu">
                                  <button onClick={() => handleDeleteSub(sub.id, sub.sub_menu_name)} style={{
                                    width: "28px", height: "28px", borderRadius: "7px", border: "1px solid #fecaca",
                                    background: "#fef2f2", color: "#dc2626", cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px",
                                  }}>
                                    <DeleteOutlined />
                                  </button>
                                </Tooltip>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add sub-menu hint when no sub-menus */}
                    {!hasSubs && (
                      <div style={{ padding: "8px 20px 10px", borderTop: "1px dashed #f0f0f0" }}>
                        <button
                          onClick={() => openAddSub(menu.id)}
                          style={{ fontSize: "12px", color: "#9ca3af", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px", padding: 0 }}
                        >
                          <PlusOutlined style={{ fontSize: "11px" }} /> Add sub-menu
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ════════════════════════════════════════════════
              Add / Edit Menu Drawer
          ════════════════════════════════════════════════ */}
          <Drawer
            title={null} placement="right" width={480}
            open={menuDrawer} onClose={() => !menuSaving && setMenuDrawer(false)}
            closable={false} styles={{ body: { padding: 0 }, header: { display: "none" } }}
          >
            {/* Header */}
            <div style={{ background: "linear-gradient(135deg,#1d4ed8,#0f172a)", padding: "22px 24px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "36px", height: "36px", background: "rgba(255,255,255,0.15)", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <MenuOutlined style={{ color: "white", fontSize: "18px" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: "16px", fontWeight: 700, color: "white" }}>{editMenuId ? "Edit Menu" : "Add Menu"}</div>
                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>{editMenuId ? "Update menu details" : "Create a new sidebar menu item"}</div>
                  </div>
                </div>
                <button onClick={() => !menuSaving && setMenuDrawer(false)} style={{ width: "32px", height: "32px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>×</button>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: "24px", overflowY: "auto", height: "calc(100vh - 148px)" }}>
              <Field label="Menu Name" required error={menuErrors.menu_name}>
                <input
                  value={menuForm.menu_name}
                  onChange={e => { setMenuForm(p => ({ ...p, menu_name: e.target.value })); if (e.target.value.trim()) setMenuErrors(p => ({ ...p, menu_name: "" })); }}
                  placeholder="e.g. Dashboard"
                  style={inputStyle(menuErrors.menu_name)}
                />
              </Field>

              <Field label="Menu Icon" hint="(FA class e.g. fa-users)">
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <input
                    value={menuForm.menu_icon}
                    onChange={e => setMenuForm(p => ({ ...p, menu_icon: e.target.value }))}
                    placeholder="fa-users"
                    style={{ ...inputStyle(false), flex: 1 }}
                  />
                  {menuForm.menu_icon && (
                    <div style={{ width: "42px", height: "42px", background: "#eff6ff", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className={`fas ${menuForm.menu_icon.startsWith("fa-") ? menuForm.menu_icon : `fa-${menuForm.menu_icon}`}`} style={{ color: "#1d4ed8", fontSize: "18px" }} />
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Menu Link" hint="(route path)">
                <input
                  value={menuForm.menu_link}
                  onChange={e => setMenuForm(p => ({ ...p, menu_link: e.target.value }))}
                  placeholder="/dashboard"
                  style={inputStyle(false)}
                />
              </Field>

              <Field label="Display Order">
                <input
                  type="number" min="0"
                  value={menuForm.menu_order}
                  onChange={e => setMenuForm(p => ({ ...p, menu_order: e.target.value }))}
                  placeholder="1"
                  style={inputStyle(false)}
                />
              </Field>

              {editMenuId && (
                <Field label="Status">
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Switch
                      checked={menuForm.status === 1}
                      onChange={v => setMenuForm(p => ({ ...p, status: v ? 1 : 0 }))}
                      style={{ background: menuForm.status === 1 ? "#16a34a" : "#d1d5db" }}
                    />
                    <span style={{ fontSize: "14px", fontWeight: 600, color: menuForm.status === 1 ? "#16a34a" : "#9ca3af" }}>
                      {menuForm.status === 1 ? "Active" : "Inactive"}
                    </span>
                  </div>
                </Field>
              )}
            </div>

            {/* Footer */}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "16px 24px", borderTop: "1px solid #f3f4f6", background: "white", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => !menuSaving && setMenuDrawer(false)} disabled={menuSaving} style={{ padding: "9px 20px", borderRadius: "9px", border: "1px solid #e5e7eb", background: "white", color: "#374151", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleSaveMenu} disabled={menuSaving} style={{
                padding: "9px 24px", borderRadius: "9px", border: "none",
                background: menuSaving ? "#93c5fd" : "linear-gradient(135deg,#1d4ed8,#0f172a)",
                color: "white", fontWeight: 700, fontSize: "13px",
                cursor: menuSaving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "7px",
              }}>
                {menuSaving && <span className="spinner-border spinner-border-sm" role="status" />}
                {menuSaving ? "Saving..." : (editMenuId ? "Update Menu" : "Create Menu")}
              </button>
            </div>
          </Drawer>

          {/* ════════════════════════════════════════════════
              Add / Edit Sub-menu Drawer
          ════════════════════════════════════════════════ */}
          <Drawer
            title={null} placement="right" width={480}
            open={subDrawer} onClose={() => !subSaving && setSubDrawer(false)}
            closable={false} styles={{ body: { padding: 0 }, header: { display: "none" } }}
          >
            {/* Header */}
            <div style={{ background: "linear-gradient(135deg,#7c3aed,#5b48e8)", padding: "22px 24px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "36px", height: "36px", background: "rgba(255,255,255,0.15)", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <AppstoreOutlined style={{ color: "white", fontSize: "18px" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: "16px", fontWeight: 700, color: "white" }}>{editSubId ? "Edit Sub-menu" : "Add Sub-menu"}</div>
                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>
                      {editSubId ? "Update sub-menu details" : `Under: ${parentMenuName}`}
                    </div>
                  </div>
                </div>
                <button onClick={() => !subSaving && setSubDrawer(false)} style={{ width: "32px", height: "32px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>×</button>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: "24px", overflowY: "auto", height: "calc(100vh - 148px)" }}>
              <Field label="Sub-menu Name" required error={subErrors.sub_menu_name}>
                <input
                  value={subForm.sub_menu_name}
                  onChange={e => { setSubForm(p => ({ ...p, sub_menu_name: e.target.value })); if (e.target.value.trim()) setSubErrors(p => ({ ...p, sub_menu_name: "" })); }}
                  placeholder="e.g. All Enquiries"
                  style={inputStyle(subErrors.sub_menu_name)}
                />
              </Field>

              <Field label="Icon" hint="(FA class)">
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <input
                    value={subForm.sub_menu_icon}
                    onChange={e => setSubForm(p => ({ ...p, sub_menu_icon: e.target.value }))}
                    placeholder="fa-list"
                    style={{ ...inputStyle(false), flex: 1 }}
                  />
                  {subForm.sub_menu_icon && (
                    <div style={{ width: "42px", height: "42px", background: "#f5f3ff", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className={`fas ${subForm.sub_menu_icon.startsWith("fa-") ? subForm.sub_menu_icon : `fa-${subForm.sub_menu_icon}`}`} style={{ color: "#7c3aed", fontSize: "18px" }} />
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Link" hint="(route path)">
                <input
                  value={subForm.sub_menu_link}
                  onChange={e => setSubForm(p => ({ ...p, sub_menu_link: e.target.value }))}
                  placeholder="/leads/enquiries"
                  style={inputStyle(false)}
                />
              </Field>

              <Field label="Display Order">
                <input
                  type="number" min="0"
                  value={subForm.sub_menu_order}
                  onChange={e => setSubForm(p => ({ ...p, sub_menu_order: e.target.value }))}
                  placeholder="1"
                  style={inputStyle(false)}
                />
              </Field>

              {editSubId && (
                <Field label="Status">
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Switch
                      checked={subForm.status === 1}
                      onChange={v => setSubForm(p => ({ ...p, status: v ? 1 : 0 }))}
                      style={{ background: subForm.status === 1 ? "#16a34a" : "#d1d5db" }}
                    />
                    <span style={{ fontSize: "14px", fontWeight: 600, color: subForm.status === 1 ? "#16a34a" : "#9ca3af" }}>
                      {subForm.status === 1 ? "Active" : "Inactive"}
                    </span>
                  </div>
                </Field>
              )}
            </div>

            {/* Footer */}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "16px 24px", borderTop: "1px solid #f3f4f6", background: "white", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => !subSaving && setSubDrawer(false)} disabled={subSaving} style={{ padding: "9px 20px", borderRadius: "9px", border: "1px solid #e5e7eb", background: "white", color: "#374151", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleSaveSub} disabled={subSaving} style={{
                padding: "9px 24px", borderRadius: "9px", border: "none",
                background: subSaving ? "#c4b5fd" : "linear-gradient(135deg,#7c3aed,#5b48e8)",
                color: "white", fontWeight: 700, fontSize: "13px",
                cursor: subSaving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "7px",
              }}>
                {subSaving && <span className="spinner-border spinner-border-sm" role="status" />}
                {subSaving ? "Saving..." : (editSubId ? "Update Sub-menu" : "Add Sub-menu")}
              </button>
            </div>
          </Drawer>

        </div>
      </div>
    </div>
  );
};

export default ManageCmsMenus;
